import type { ReactNode } from 'react';
import { Globe, Smartphone, Store, MessageCircle } from 'lucide-react';
import { Tag } from '../ui';
import type { TagTone } from '../ui';

/**
 * How the order reached us, as a pill that shows the route rather than spelling it in caps.
 *
 * `WEB` on its own is a word the reader has to translate; the glyph is the thing they recognise. The
 * icon VARIES with the channel — a globe for the web desk, a phone for the mobile app, a storefront
 * for a dealer's own app, a message bubble for WhatsApp — so it carries information instead of being
 * the same decoration on every order (see the "constant glyph marks are noise" rule).
 *
 * Lives here, not on a page, because the order DETAIL and the order LIST both name the channel and
 * had drifted: the detail page showed this pill while the list printed the raw enum. One definition
 * means a new channel appears in both places at once.
 */
export const CHANNELS: Record<string, { label: string; tone: TagTone; icon: ReactNode }> = {
  WEB: { label: 'Web', tone: 'blue', icon: <Globe /> },
  MOBILE: { label: 'Mobile app', tone: 'purple', icon: <Smartphone /> },
  RETAILER_APP: { label: 'Retailer app', tone: 'cyan', icon: <Store /> },
  WHATSAPP: { label: 'WhatsApp', tone: 'green', icon: <MessageCircle /> },
};

export function ChannelTag({ channel }: { channel?: string | null }) {
  if (!channel) return <span className="text-subtle">—</span>;
  // An unknown channel still renders: its own code, neutral, so a new backend value shows up as
  // itself rather than vanishing from the page.
  const c = CHANNELS[String(channel).toUpperCase()];
  return <Tag tone={c?.tone ?? 'neutral'} icon={c?.icon ?? <Globe />}>{c?.label ?? channel}</Tag>;
}
